import { usePageMeta } from "./pageMeta.ts";
import { Support } from "./support.tsx";

const sectionStyle =
  "mb-6 text-text-secondary-light dark:text-text-secondary leading-relaxed text-sm";
const headingStyle =
  "text-lg font-semibold mt-8 mb-3 text-text-primary-light dark:text-text-primary";

export const PrivacyPolicy = () => {
  usePageMeta({
    title: "Privacy Policy | prompt2bot",
    description:
      "How prompt2bot collects, uses, and protects your information.",
  });
  return (
    <div className="max-w-2xl mx-auto my-10 p-6 rounded-lg shadow-md bg-surface-raised-light dark:bg-surface-raised border border-border-subtle-light dark:border-border-subtle text-text-primary-light dark:text-text-primary">
      <h1 className="text-2xl font-bold mb-2 text-center text-text-primary-light dark:text-text-primary">
        Privacy Policy
      </h1>
      <p className="text-center text-xs text-text-tertiary-light dark:text-text-tertiary mb-8">
        Last updated: May 25, 2026
      </p>

      <p className={sectionStyle}>
        This Privacy Policy describes how prompt2bot ("we", "us", "our")
        collects, uses, stores, and protects your information when you use the
        prompt2bot platform and services ("Service"). By using the Service, you
        consent to the practices described in this policy.
      </p>

      <h2 className={headingStyle}>1. Information We Collect</h2>
      <div className={sectionStyle}>
        <p className="mb-3">
          We collect the following categories of information:
        </p>
        <ul className="list-disc list-inside space-y-2">
          <li>
            <strong>Account Information:</strong>{" "}
            Email address and authentication credentials when you create an
            account.
          </li>
          <li>
            <strong>Bot Configuration Data:</strong>{" "}
            Prompts, settings, integrations, and other content you provide when
            creating and configuring bots.
          </li>
          <li>
            <strong>Conversation Data:</strong>{" "}
            Messages exchanged between your bots and their end users, including
            text, attachments, and metadata (timestamps, channel identifiers).
          </li>
          <li>
            <strong>Usage Data:</strong>{" "}
            Information about how you interact with the Service, including pages
            visited, features used, bot activity metrics, and error logs.
          </li>
          <li>
            <strong>Payment Information:</strong>{" "}
            Billing details are processed by our third-party payment provider
            (Polar / Stripe). We do not store full credit card numbers on our
            servers.
          </li>
          <li>
            <strong>Third-Party Account Data:</strong>{" "}
            When you connect external services (Google, Telegram, WhatsApp,
            Facebook, etc.), we receive and store authentication tokens and
            necessary identifiers to operate integrations on your behalf.
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>2. How We Use Your Information</h2>
      <div className={sectionStyle}>
        <ul className="list-disc list-inside space-y-2">
          <li>To provide, maintain, and improve the Service.</li>
          <li>
            To process and deliver conversations between your bots and their end
            users.
          </li>
          <li>
            To debug issues, monitor performance, and improve service
            reliability.
          </li>
          <li>To process payments and manage your subscription.</li>
          <li>
            To send you service-related communications, including updates,
            security alerts, and support messages.
          </li>
          <li>
            To detect, prevent, and address fraud, abuse, or violations of our
            Terms of Service.
          </li>
          <li>To comply with legal obligations.</li>
        </ul>
      </div>

      <h2 className={headingStyle}>3. How We Share Your Information</h2>
      <div className={sectionStyle}>
        <p className="mb-3">
          We do not sell your personal information, and we do not transfer it to
          third parties for advertising, data brokering, credit assessment,
          lending, or AI model training. We may share your information in the
          following circumstances:
        </p>
        <ul className="list-disc list-inside space-y-2">
          <li>
            <strong>AI Model Providers:</strong>{" "}
            Conversation content is sent to third-party AI model providers (such
            as Google and OpenAI) solely to generate bot responses in real time.
            We use these providers under terms that prohibit the use of your
            data, including Google user data, for training their models. These
            providers process data according to their own privacy policies and
            data processing agreements.
          </li>
          <li>
            <strong>Channel Platforms:</strong>{" "}
            When your bots operate on third-party platforms (Telegram, WhatsApp,
            Facebook Messenger, etc.), message data is transmitted through those
            platforms according to their respective privacy policies.
          </li>
          <li>
            <strong>Payment Processors:</strong>{" "}
            Billing information is shared with our payment processor to process
            transactions.
          </li>
          <li>
            <strong>Legal Requirements:</strong>{" "}
            We may disclose information when required by law, in response to
            valid legal process, or to protect the rights, property, or safety
            of prompt2bot, our users, or the public.
          </li>
          <li>
            <strong>Law Enforcement:</strong>{" "}
            We will report illegal content (including child sexual abuse
            material) to the appropriate authorities, including the National
            Center for Missing & Exploited Children (NCMEC), and preserve
            associated data as required.
          </li>
        </ul>
      </div>

      <h2 className={headingStyle}>
        4. Data Access and Operational Interventions
      </h2>
      <p className={sectionStyle}>
        Conversation data processed through your bots is accessible to you (the
        bot owner) through the dashboard and API. Our team may also access,
        review, and act on conversation data — including reading messages,
        inspecting bot reasoning and tool calls, injecting corrective context or
        instructions into a live conversation, pausing or modifying bot
        behavior, and otherwise intervening in conversations — for purposes
        including debugging, support, abuse prevention, security, quality
        assurance, hallucination correction, and service improvement. Such
        interventions may be visible to end users (for example, as a message
        from the bot prompted by our internal correction). End users of your
        bots should be informed by you that their conversations are processed by
        an AI service, are accessible to the bot owner and to prompt2bot
        personnel, and may be subject to operational interventions by prompt2bot
        to maintain service quality and safety.
      </p>

      <h2 className={headingStyle}>5. Data Retention</h2>
      <p className={sectionStyle}>
        We retain your data for as long as your account is active or as needed
        to provide the Service. Conversation data is retained as long as
        necessary for bot operation and service improvement. Upon account
        deletion, we will delete or anonymize your personal data within a
        reasonable timeframe, except where retention is required by law or
        necessary for legitimate business purposes (such as resolving disputes
        or enforcing our Terms).
      </p>

      <h2 className={headingStyle}>6. Data Security</h2>
      <p className={sectionStyle}>
        We implement industry-standard technical and organizational measures to
        protect your data from unauthorized access, alteration, disclosure, or
        destruction. These include encryption of data in transit (TLS), access
        controls, and secure credential storage. However, no method of
        transmission or storage is completely secure, and we cannot guarantee
        absolute security.
      </p>

      <h2 className={headingStyle}>7. Cookies and Analytics</h2>
      <p className={sectionStyle}>
        We use analytics services (such as PostHog) to understand how the
        Service is used and to improve it. These services may collect usage data
        including pages visited, features used, and interaction patterns. We do
        not send data obtained from Google APIs (or other OAuth-connected
        accounts) to analytics providers, and we do not use advertising cookies
        or tracking pixels for ad targeting.
      </p>

      <h2 className={headingStyle}>
        8. Limitations on Use of Google User Data
      </h2>
      <div className={sectionStyle}>
        <p className="mb-3">
          Our use and transfer of information received from Google APIs to any
          other app adheres to the{" "}
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            className="underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>, including the Limited Use requirements.
        </p>

        <p className="font-semibold mt-4 mb-2">Google Data Collection:</p>
        <p className="mb-3">
          When you connect your Google account or use Google-integrated
          services, we may access and collect Google user data. This includes:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>
            Google account profile information (such as email address, name, and
            profile picture) to authenticate your identity.
          </li>
          <li>
            Authentication tokens and integration metadata required to operate
            the connected Google integrations.
          </li>
          <li>
            Specific contents and resources (such as Google Sheets data,
            Calendar events, Gmail messages, Docs, and files in Google Drive)
            that you explicitly authorize your bots to access, read, create, or
            modify on your behalf.
          </li>
        </ul>

        <p className="font-semibold mt-4 mb-2">Google Data Usage:</p>
        <p className="mb-3">
          We use Google user data solely to:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>
            Provide, operate, and maintain the integrations you configure (e.g.,
            allowing your bots to read Sheets, schedule events, or send emails
            on your behalf).
          </li>
          <li>Authenticate your account and verify access.</li>
          <li>
            Improve and optimize the user-facing functionality of the Service.
          </li>
        </ul>

        <p className="font-semibold mt-4 mb-2">
          Google Data Sharing & Disclosure:
        </p>
        <p className="mb-3">
          We do not sell Google user data to third parties, and we do not
          transfer or disclose Google user data for reasons other than providing
          or improving your application's functionality. Specifically, Google
          user data is never used or transferred for:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>
            Serving targeted, personalized, retargeted, or interest-based
            advertisements.
          </li>
          <li>Selling to data brokers or information resellers.</li>
          <li>Determining credit-worthiness or for lending purposes.</li>
          <li>
            Creating databases for other applications or commercial purposes.
          </li>
          <li>Training machine learning or AI models.</li>
        </ul>
        <p className="mb-3">
          Google user data is never shared with humans, except with your
          explicit consent, as required for security purposes (such as
          investigating abuse), or to comply with applicable law.
        </p>

        <p className="font-semibold mt-4 mb-2">
          Google Data Protection & Security:
        </p>
        <p className="mb-3">
          We implement rigorous security procedures and data protection
          mechanisms to protect the confidentiality and integrity of Google user
          data:
        </p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>
            All Google user data, including OAuth tokens and credentials, is
            encrypted in transit using Transport Layer Security (TLS) and
            encrypted at rest using strong industry-standard encryption
            algorithms.
          </li>
          <li>
            We employ secure, restricted credential storage and access control
            mechanisms to prevent unauthorized access.
          </li>
        </ul>

        <p className="font-semibold mt-4 mb-2">
          Google Data Retention & Deletion:
        </p>
        <p className="mb-3">
          We retain Google user data (including OAuth tokens) only for as long
          as your account is active and connected, or as needed to provide the
          requested integrations. You can disconnect your Google account or
          request the deletion of your account at any time. Upon account
          deletion or disconnection, we will securely delete or anonymize your
          Google user data and credentials from our active databases within a
          reasonable timeframe, unless a longer retention period is required by
          law.
        </p>
      </div>

      <h2 className={headingStyle}>9. Third-Party Services</h2>
      <p className={sectionStyle}>
        The Service integrates with third-party platforms and providers. When
        you connect external services or your bots operate on third-party
        channels, your data is subject to the privacy policies of those
        providers in addition to this policy. We encourage you to review the
        privacy policies of any third-party services you use in connection with
        prompt2bot.
      </p>

      <h2 className={headingStyle}>10. Children's Privacy</h2>
      <p className={sectionStyle}>
        The Service is not directed to individuals under 18 years of age. We do
        not knowingly collect personal information from children. If we become
        aware that we have collected personal information from a child under 18,
        we will take steps to delete that information promptly.
      </p>

      <h2 className={headingStyle}>11. International Data Transfers</h2>
      <p className={sectionStyle}>
        Your information may be transferred to and processed in countries other
        than your country of residence, including the United States. These
        countries may have data protection laws that differ from your
        jurisdiction. By using the Service, you consent to the transfer of your
        information to these countries.
      </p>

      <h2 className={headingStyle}>12. Your Rights</h2>
      <div className={sectionStyle}>
        <p className="mb-3">
          Depending on your jurisdiction, you may have the following rights
          regarding your personal data:
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Access:</strong>{" "}
            Request a copy of the personal data we hold about you.
          </li>
          <li>
            <strong>Correction:</strong>{" "}
            Request correction of inaccurate or incomplete data.
          </li>
          <li>
            <strong>Deletion:</strong>{" "}
            Request deletion of your personal data, subject to legal retention
            requirements.
          </li>
          <li>
            <strong>Portability:</strong>{" "}
            Request your data in a structured, machine-readable format.
          </li>
          <li>
            <strong>Objection:</strong>{" "}
            Object to certain processing of your data.
          </li>
        </ul>
        <p className="mt-3">
          To exercise any of these rights, contact us using the information
          below. We will respond within a reasonable timeframe.
        </p>
      </div>

      <h2 className={headingStyle}>13. Changes to This Policy</h2>
      <p className={sectionStyle}>
        We may update this Privacy Policy from time to time. We will notify you
        of material changes by posting the updated policy on this page with a
        new "Last updated" date. Continued use of the Service after changes
        constitutes acceptance of the updated policy.
      </p>

      <h2 className={headingStyle}>14. Contact</h2>
      <p className={sectionStyle}>
        If you have questions about this Privacy Policy or wish to exercise your
        data rights, please contact us:
      </p>
      <div className="text-sm">
        <Support />
      </div>
    </div>
  );
};
